/**
 * The operational facts of a trip, editable by admins AND the concierge agent.
 *
 * Six fields, and only six: the airline confirmation number, Ticketed, Agency,
 * trip status, what the ticket actually cost us, and seat assignments. They
 * are what someone fulfilling a booking needs to record; everything else about
 * a booking (fares, passengers, payment plans) stays in the admin edit modal
 * behind `canEditBookings`.
 *
 * This panel exists rather than opening that modal to the concierge because
 * the narrow surface IS the permission: the server endpoint it calls names
 * every field it will write, so there is nothing here to widen by accident.
 *
 * Status and actual cost are sent only when changed here (2026-10-27): the
 * panel used to send its status on every save, from a value that could be
 * stale, and saving a seat flipped a cancelled-for-credit trip back to
 * confirmed. A status change also carries the status this panel loaded, so
 * the server refuses it (409) if the trip moved in the meantime. The
 * concierge cannot set Cancelled or Defaulted, or move a trip out of
 * Completed, Cancelled or Defaulted: those run side effects only the admin
 * edit form performs. It can set Completed, and the server allows it only
 * when the trip is paid in full and has an airline confirmation number
 * (its refusal shows here as the error).
 */
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader2, Check } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/** Terminal statuses: the concierge never moves a trip out of one. */
const MONEY_STATUSES = new Set(['completed', 'cancelled', 'defaulted']);
/** The concierge never sets these (Completed it may, behind the server's paid-in-full check). */
const ADMIN_ONLY_TARGETS = new Set(['cancelled', 'defaulted']);

const STATUSES = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'delinquent', label: 'Delinquent' },
  { value: 'defaulted', label: 'Defaulted' },
];

const INPUT =
  'h-9 rounded-lg bg-black/30 border border-white/15 px-2.5 text-[13px] text-white placeholder:text-white/30 focus:outline-none focus:border-emerald-400/50';

export default function TripOpsPanel({ trip, onSaved, canRemoveConfirmation = false, canSetMoneyStatus = false }) {
  const main = trip?.[0] || null;
  const flightData = useMemo(() => main?.flightData || main?.flight_data || {}, [main]);

  const initialConfirmation = main?.confirmationNumber || main?.confirmation_number || flightData.confirmation_number || '';
  const [confirmation, setConfirmation] = useState(initialConfirmation);
  /** The number on file as of the last load or save — what a blank field removes. */
  const [savedConfirmation, setSavedConfirmation] = useState(initialConfirmation);
  /** Removal asks once before it goes: it flips every "is this ticketed?" rule. */
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);
  // The status COLUMN first: `trip_status` can come from a stale
  // flight_data.trip_status (src/api/entities.js).
  const initialStatus = main?.status || main?.trip_status || 'pending';
  const [status, setStatus] = useState(initialStatus);
  /** The status as of the last load or save — sent as expected_status. */
  const [loadedStatus, setLoadedStatus] = useState(initialStatus);
  const [ticketed, setTicketed] = useState(Boolean(flightData.is_ticketed));
  const [agency, setAgency] = useState(Boolean(flightData.is_agency));
  const initialActualCost = flightData._actual_cost != null ? String(flightData._actual_cost) : '';
  const [actualCost, setActualCost] = useState(initialActualCost);
  const [loadedActualCost, setLoadedActualCost] = useState(initialActualCost);
  /** Concierge: a money status is never offered, and a trip already in one is read-only here. */
  const statusLocked = !canSetMoneyStatus && MONEY_STATUSES.has(loadedStatus);
  const [seats, setSeats] = useState(() => flightData.seat_assignments || {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  /** Every (passenger × segment) pair this trip actually has — the same ids
   *  the server validates against, so the UI can't offer an invalid one. */
  const seatRows = useMemo(() => {
    const passengers = Array.isArray(main?.passengers) ? main.passengers : [];
    const segments = (flightData.slices || []).flatMap((slice) => slice?.segments || []);
    return passengers.flatMap((p) =>
      segments
        .filter((seg) => seg?.id)
        .map((seg) => ({
          passengerId: p.id,
          passengerName: [p.first_name || p.firstName, p.last_name || p.lastName].filter(Boolean).join(' ') || 'Passenger',
          segmentId: seg.id,
          segmentLabel: `${seg.origin?.iata_code || '?'}→${seg.destination?.iata_code || '?'}${
            seg.marketing_carrier_flight_number ? ` · ${seg.marketing_carrier_flight_number}` : ''
          }`,
        })),
    );
  }, [main, flightData]);

  const setSeat = (passengerId, segmentId, value) =>
    setSeats((prev) => ({ ...prev, [passengerId]: { ...(prev[passengerId] || {}), [segmentId]: value } }));

  /** A blank field on a trip that has a number is a REMOVAL, not "unchanged".
   *  This panel used to drop the blank without a word and still report Saved,
   *  so the number silently stayed on the trip. */
  const removingConfirmation = !confirmation.trim() && Boolean(String(savedConfirmation).trim());

  const save = async () => {
    if (!main?.id || saving) return;
    if (removingConfirmation) {
      if (!canRemoveConfirmation) {
        setError('Only an admin can remove a confirmation number.');
        return;
      }
      if (!confirmingRemoval) {
        setSavedAt(null); // a stale "Saved" must not read as the removal having happened
        setConfirmingRemoval(true);
        return;
      }
    }
    setSaving(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const body = {
        bookingId: main.id,
        is_ticketed: ticketed,
        is_agency: agency,
      };
      // Only what changed here, and a status change names the status it
      // replaces so the server can refuse a stale one.
      if (status !== loadedStatus) {
        body.status = status;
        body.expected_status = loadedStatus;
      }
      // '' clears it — "we don't know yet" is a real state for a cost.
      if (actualCost.trim() !== loadedActualCost.trim()) {
        body.actual_cost = actualCost.trim() === '' ? '' : actualCost.trim();
      }
      // A blank is sent only as a confirmed admin removal (null). The server
      // clears every copy of the number and keeps the old one on record.
      if (confirmation.trim()) body.confirmation_number = confirmation.trim();
      else if (removingConfirmation) body.confirmation_number = null;
      if (seatRows.length > 0) body.seat_assignments = seats;

      const res = await fetch('/api/admin/trip-ops-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'Could not save');
      setSavedConfirmation(data?.booking?.confirmation_number || '');
      if (data?.booking?.status) {
        setLoadedStatus(data.booking.status);
        setStatus(data.booking.status);
      }
      const savedCost = data?.booking?.actual_cost != null ? String(data.booking.actual_cost) : '';
      setLoadedActualCost(savedCost);
      setActualCost(savedCost);
      setConfirmingRemoval(false);
      setSavedAt(Date.now());
      onSaved?.(data.booking);
    } catch (e) {
      setError(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!main) return null;

  return (
    <div className="rounded-2xl border border-white/10 bg-black/20 px-4 py-4 space-y-3.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-white/40 text-[10px] uppercase tracking-[0.14em] font-normal">Ops · trip record</span>
        {savedAt && !saving && (
          <span className="flex items-center gap-1 text-emerald-300/80 text-[11.5px] font-normal">
            <Check className="w-3 h-3" /> Saved
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        <label className="space-y-1">
          <span className="text-white/50 text-[11.5px] font-normal">Airline confirmation #</span>
          <input
            type="text"
            value={confirmation}
            onChange={(e) => {
              setConfirmation(e.target.value);
              setConfirmingRemoval(false);
              setError(null);
            }}
            placeholder="e.g. H8H6M3"
            className={`${INPUT} w-full`}
          />
        </label>
        <label className="space-y-1">
          <span className="text-white/50 text-[11.5px] font-normal">Trip status</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={statusLocked}
            className={`${INPUT} w-full disabled:opacity-60`}
          >
            {STATUSES.map((s) => (
              <option
                key={s.value}
                value={s.value}
                disabled={!canSetMoneyStatus && ADMIN_ONLY_TARGETS.has(s.value) && s.value !== loadedStatus}
                className="bg-zinc-900"
              >
                {s.label}
              </option>
            ))}
          </select>
          {!canSetMoneyStatus && (
            <span className="block text-white/35 text-[11px] font-normal">
              {statusLocked
                ? `Only an admin can change a ${loadedStatus} trip's status.`
                : 'Completed needs the trip paid in full and an airline confirmation number on file. Cancelled and Defaulted are set by an admin.'}
            </span>
          )}
        </label>
        <label className="space-y-1">
          <span className="text-white/50 text-[11.5px] font-normal">Actual cost (what we paid)</span>
          <input
            type="text"
            inputMode="decimal"
            value={actualCost}
            onChange={(e) => setActualCost(e.target.value.replace(/[^0-9.]/g, ''))}
            placeholder="blank = not known yet"
            className={`${INPUT} w-full`}
          />
        </label>
        <div className="flex items-end gap-2">
          {[
            { label: 'Ticketed', value: ticketed, set: setTicketed },
            { label: 'Agency', value: agency, set: setAgency },
          ].map((t) => (
            <button
              key={t.label}
              type="button"
              onClick={() => t.set(!t.value)}
              aria-pressed={t.value}
              className={`h-9 px-3.5 rounded-lg border text-[13px] font-normal transition-colors ${
                t.value
                  ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-200'
                  : 'bg-white/[0.04] border-white/10 text-white/50 hover:text-white/80'
              }`}
            >
              {t.label}: {t.value ? 'Yes' : 'No'}
            </button>
          ))}
        </div>
      </div>

      {seatRows.length > 0 && (
        <div className="space-y-1.5 pt-1">
          <span className="text-white/50 text-[11.5px] font-normal">Seats</span>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {seatRows.map((row) => (
              <label key={`${row.passengerId}:${row.segmentId}`} className="flex items-center gap-2">
                <span className="text-white/45 text-[11.5px] font-normal min-w-0 flex-1 truncate">
                  {row.passengerName} · {row.segmentLabel}
                </span>
                <input
                  type="text"
                  value={seats?.[row.passengerId]?.[row.segmentId] || ''}
                  onChange={(e) => setSeat(row.passengerId, row.segmentId, e.target.value)}
                  placeholder="12A"
                  aria-label={`Seat for ${row.passengerName} on ${row.segmentLabel}`}
                  className={`${INPUT} w-20 shrink-0`}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-rose-300 text-[12.5px] font-normal">{error}</p>}

      {confirmingRemoval && removingConfirmation && (
        <div role="alert" className="rounded-xl border border-amber-400/30 bg-amber-500/[0.08] px-3.5 py-3 space-y-2.5">
          <p className="text-amber-100/90 text-[12.5px] font-normal leading-snug">
            Remove confirmation # <span className="font-medium text-amber-50">{savedConfirmation}</span>?
            Rules that check for an airline confirmation (declined-payment retries, cancellation credit)
            will treat this trip as unticketed, and the customer stops seeing it on their trip.
            {ticketed && ' Ticketed is still set to Yes — switch it off too if there is no ticket.'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="h-8 px-3.5 rounded-full bg-amber-300 text-black text-[12.5px] font-medium hover:bg-amber-200 transition-colors disabled:opacity-50"
            >
              Remove and save
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmation(savedConfirmation);
                setConfirmingRemoval(false);
              }}
              disabled={saving}
              className="h-8 px-3.5 rounded-full border border-white/15 text-white/70 text-[12.5px] font-normal hover:text-white transition-colors"
            >
              Keep it
            </button>
          </div>
        </div>
      )}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="h-9 px-4 rounded-full bg-white text-black text-[13px] font-medium hover:bg-white/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" />Saving…</> : 'Save trip record'}
        </button>
        <span className="text-white/30 text-[11px] font-normal">
          Saving seats for the first time emails the customer their seat numbers.
        </span>
      </div>
    </div>
  );
}

TripOpsPanel.propTypes = {
  trip: PropTypes.arrayOf(PropTypes.object),
  onSaved: PropTypes.func,
  /** Admins only — the concierge can set a number but never remove one. */
  canRemoveConfirmation: PropTypes.bool,
  /** Admins only: Cancelled / Defaulted, and leaving a terminal status. Without it Completed is offered behind the server's paid-in-full check. */
  canSetMoneyStatus: PropTypes.bool,
};
